import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-5, 5]), { stiffness: 120, damping: 18 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const resetTilt = () => {
    mx.set(0);
    my.set(0);
  };

  const go = (id) => {
    if (window.__lenis) {
      window.__lenis.scrollTo(`#${id}`, { offset: -72, duration: 1.4 });
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      ref={sectionRef}
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden pt-16"
    >
      <div
        className="pointer-events-none absolute -right-40 top-1/4 h-[560px] w-[560px] rounded-full opacity-60 blur-[140px]"
        style={{ background: "radial-gradient(circle, rgba(204,10,20,0.22), transparent 65%)" }}
      />

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-24 pt-16 sm:px-8 lg:grid-cols-12 lg:pt-24">
        <motion.div style={{ y: textY, opacity: fade }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="mb-8 flex flex-wrap items-center gap-4"
          >
            <span className="flex items-center gap-2 border border-[#27272A] bg-[#121214] px-3 py-1.5">
              <span className="h-1.5 w-1.5 animate-pulse-red rounded-full bg-[#CC0A14]" />
              <span className="font-jb text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                Protocol Active
              </span>
            </span>
            <span className="font-jb text-[11px] uppercase tracking-[0.25em] text-zinc-500">
              Est. 2026 — United Kingdom
            </span>
          </motion.div>

          <h1 data-testid="hero-heading" className="sr-only">
            Sharpen the jaw. Eat real food.
          </h1>
          <MaskedLines
            lines={["SHARPEN", "THE JAW.", "EAT REAL", "FOOD."]}
            className="font-syne text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[1.02] tracking-tight text-white"
            lineClassName=""
          />
          <div className="mt-2 font-syne text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase leading-[1.02] tracking-tight text-[#CC0A14]">
            <div className="overflow-hidden py-[0.06em]">
              <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              >
                NOTHING PROCESSED.
              </motion.span>
            </div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
          >
            BP Club is for men rebuilding what the modern world softened — a
            structured jawline through masseter training, and a clean face
            through ancestral, real-food eating. No powders. No surgery. No
            shortcuts.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => go("programs")}
              className="group flex items-center gap-3 border border-[#CC0A14] bg-[#CC0A14] px-7 py-4 font-jb text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-transparent hover:text-[#CC0A14]"
            >
              See the Programs
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-cta-secondary"
              onClick={() => go("manifesto")}
              className="group flex items-center gap-3 border border-[#27272A] px-7 py-4 font-jb text-xs font-semibold uppercase tracking-[0.22em] text-zinc-300 transition-colors duration-300 hover:border-[#CC0A14] hover:text-white"
            >
              Read the Manifesto
              <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="mt-14 flex divide-x divide-[#27272A] border-y border-[#27272A]"
          >
            {[
              ["02", "Core protocols"],
              ["12wk", "Training cycles"],
              ["00", "Seed oils tolerated"],
            ].map(([v, l]) => (
              <div key={l} className="flex-1 px-5 py-5 first:pl-0">
                <p className="font-syne text-2xl font-extrabold text-white sm:text-3xl">{v}</p>
                <p className="mt-1 font-jb text-[10px] uppercase tracking-[0.2em] text-zinc-500">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="relative lg:col-span-5"
          onMouseMove={onTilt}
          onMouseLeave={resetTilt}
        >
          <motion.div
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className="relative border border-[#7F1D1D]/50"
          >
            <div className="absolute -left-2.5 -top-2.5 z-10 h-6 w-6 border-l-2 border-t-2 border-[#CC0A14]" />
            <div className="absolute -bottom-2.5 -right-2.5 z-10 h-6 w-6 border-b-2 border-r-2 border-[#CC0A14]" />
            <div className="relative aspect-[4/5] overflow-hidden bg-[#121214]">
              <motion.img
                src={IMAGES.heroPortrait}
                alt="Male profile with defined jawline in dramatic light"
                data-testid="hero-portrait"
                style={{ y: imgY, scale: 1.15 }}
                className="h-full w-full object-cover grayscale"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#080808] via-transparent to-transparent opacity-80" />
            </div>
            <div className="flex items-center justify-between border-t border-[#27272A] bg-[#0c0c0e] px-4 py-3">
              <span className="font-jb text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                Fig. 01 — The structure
              </span>
              <span className="font-jb text-[10px] uppercase tracking-[0.22em] text-[#CC0A14]">
                Built daily
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
